import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { HandThumbUpIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import {
    AccountRecord,
    ContentAction,
    ContentActionView,
    ContentView,
    useLikeContentMutation,
    useUpdateEngageAccountIdMutation
} from '../../../api/client'
import { isActionApplied } from '../../content/content-card/actions'
import { toastError, toastSuccess, toastWarning } from '../../Notification'
import { canUseCrossProfileEngageState } from '../../../store/DomainStore'
import CrossProfileEngageSelectionComponent from '../CrossProfileEngageSelectionComponent'
import BaseActionButton from './BaseActionButton'
import { getAccountDisplayName } from '../../../util/account-utils'

type Props = {
    contents: ContentView[]
    onActionClick: (actions: ContentActionView[], queued?: boolean) => void
    actions: ContentActionView[]
    // actionCacheMap: Map<number, ContentActionView[]>
    disabled?: boolean
    disabledMessage?: string
}

export default function LikeButton(props: Props) {
    const { contents, onActionClick, actions, disabled, disabledMessage } = props

    const [updateEngageAccountIdMutation] = useUpdateEngageAccountIdMutation()

    const suppressLikeSuccessToastRef = useRef(false)

    const [likeContentMutation, { loading }] = useLikeContentMutation({
        onCompleted: (data) => {
            const likeResponses = data.likeContent ?? []

            if (likeResponses.length > 0) {
                const contentTotal = likeResponses.length
                const contentActioned = likeResponses.filter(
                    (contentAction) => contentAction.success
                ).length
                const contentNotActioned = likeResponses.filter(
                    (contentAction) => !contentAction.success
                ).length

                const allSuccess = likeResponses.every((contentAction) => contentAction.success)
                const allFailure = likeResponses.every((contentAction) => !contentAction.success)

                if (allSuccess && !suppressLikeSuccessToastRef.current) {
                    toastSuccess(`${contentTotal} item(s) liked`)
                }

                if (allFailure) {
                    toastError(`Unable to like any content.`)
                }

                if (!allSuccess && !allFailure) {
                    toastWarning(
                        `Liked ${contentActioned}/${contentTotal} items. \n\nFailed to like ${contentNotActioned} items. \n\nLike may not be supported on this platform, these items may have been removed or there may be a problem with authroisation`
                    )
                }

                const contentActions = likeResponses
                    .filter(
                        (contentAction) =>
                            contentAction !== null &&
                            contentAction !== undefined &&
                            contentAction.success
                    )
                    .map((contentAction) => contentAction.contentAction as ContentActionView)

                onActionClick(contentActions)

                const anyFailure = likeResponses.some(
                    (contentAction) =>
                        contentAction !== null &&
                        contentAction !== undefined &&
                        !contentAction.success
                )

                if (anyFailure) {
                    restorePendingLikeState()
                } else {
                    const target = pendingTargetRef.current
                    if (target) {
                        setLocalLikeApplied(target.likeApplied)
                        setLocalLikedAccountId(target.likedAccountId)
                    }
                    clearPendingLikeState()
                }
            } else {
                restorePendingLikeState()
            }
            suppressLikeSuccessToastRef.current = false
        },
        onError: (error) => {
            suppressLikeSuccessToastRef.current = false
            toastError(error.message)
            restorePendingLikeState()
        }
    })

    const actionAlreadyApplied = isActionApplied(actions, ContentAction.Like)

    const canUseCrossProfileEngage = useRecoilValue(canUseCrossProfileEngageState)

    // Cross Profile Engage is not supported for bulk like at the moment.

    const primaryAccount: AccountRecord | undefined = useMemo(() => {
        if (contents.length !== 1) {
            return undefined
        }

        return contents[0]?.account
    }, [contents])

    const activeLikeAccountId = useMemo(() => {
        if (contents.length !== 1) {
            return undefined
        }

        const content = contents[0]
        if (!content) {
            return undefined
        }

        const activeLikeAction = content.actions.find(
            (contentAction) =>
                contentAction.action.toLowerCase() === ContentAction.Like.toLowerCase() &&
                contentAction.active
        )

        // Prefer the account that actually performed the like, if available.
        return activeLikeAction?.actionedByAccountId ?? undefined
    }, [contents])

    const [selectedCrossProfileAccountId, setSelectedCrossProfileAccountId] = useState<
        number | undefined
    >(primaryAccount?.engageAccountId ?? primaryAccount?.id)
    const [defaultCrossProfileAccountId, setDefaultCrossProfileAccountId] = useState<
        number | undefined
    >(primaryAccount?.engageAccountId ?? undefined)
    const [isContainerHovered, setIsContainerHovered] = useState(false)

    const remoteLikedAccountId = actionAlreadyApplied ? activeLikeAccountId : undefined

    // Track optimistic like/unlike state so the UI can react before the backend response arrives.
    const [localLikeApplied, setLocalLikeApplied] = useState<boolean | undefined>(undefined)
    const [localLikedAccountId, setLocalLikedAccountId] = useState<number | undefined>(
        remoteLikedAccountId
    )

    const effectiveActionAlreadyApplied = localLikeApplied ?? actionAlreadyApplied
    const effectiveLikedAccountId =
        localLikeApplied === undefined
            ? remoteLikedAccountId
            : localLikeApplied
              ? (localLikedAccountId ?? selectedCrossProfileAccountId)
              : undefined

    const pendingSnapshotRef = useRef<{
        localLikeApplied: boolean | undefined
        localLikedAccountId: number | undefined
        selectedAccountId: number | undefined
    } | null>(null)
    const pendingTargetRef = useRef<{
        likeApplied: boolean
        likedAccountId: number | undefined
    } | null>(null)

    const restorePendingLikeState = useCallback(() => {
        const snapshot = pendingSnapshotRef.current
        if (!snapshot) {
            return
        }

        setLocalLikeApplied(snapshot.localLikeApplied)
        setLocalLikedAccountId(snapshot.localLikedAccountId)
        setSelectedCrossProfileAccountId(snapshot.selectedAccountId)
        pendingSnapshotRef.current = null
        pendingTargetRef.current = null
    }, [])

    const clearPendingLikeState = useCallback(() => {
        pendingSnapshotRef.current = null
        pendingTargetRef.current = null
    }, [])

    const singleContentId = contents.length === 1 ? contents[0]?.id : undefined
    const previousContentIdRef = useRef<number | undefined>(singleContentId)
    const previousEngageAccountIdRef = useRef<number | undefined>(
        primaryAccount?.engageAccountId ?? undefined
    )

    useEffect(() => {
        const currentEngageAccountId = primaryAccount?.engageAccountId ?? undefined
        const fallbackSelectedAccountId = currentEngageAccountId ?? primaryAccount?.id
        const fallbackDefaultAccountId = currentEngageAccountId

        const contentChanged = previousContentIdRef.current !== singleContentId
        const engageAccountChanged = previousEngageAccountIdRef.current !== currentEngageAccountId

        previousContentIdRef.current = singleContentId
        previousEngageAccountIdRef.current = currentEngageAccountId

        if (!contentChanged && !engageAccountChanged) {
            return
        }

        setSelectedCrossProfileAccountId(fallbackSelectedAccountId)
        setDefaultCrossProfileAccountId(fallbackDefaultAccountId)
        setLocalLikeApplied(undefined)
        setLocalLikedAccountId(remoteLikedAccountId)
        clearPendingLikeState()
    }, [
        clearPendingLikeState,
        primaryAccount?.engageAccountId,
        primaryAccount?.id,
        remoteLikedAccountId,
        singleContentId
    ])

    useEffect(() => {
        if (pendingSnapshotRef.current !== null) {
            return
        }

        setLocalLikeApplied(undefined)
        setLocalLikedAccountId(remoteLikedAccountId)
    }, [remoteLikedAccountId])

    useEffect(() => {
        if (pendingSnapshotRef.current !== null) {
            return
        }

        if (effectiveActionAlreadyApplied) {
            return
        }

        const fallbackAccountId =
            defaultCrossProfileAccountId ?? primaryAccount?.engageAccountId ?? primaryAccount?.id

        if (fallbackAccountId !== undefined) {
            setSelectedCrossProfileAccountId(fallbackAccountId)
        }
    }, [
        effectiveActionAlreadyApplied,
        defaultCrossProfileAccountId,
        primaryAccount?.engageAccountId,
        primaryAccount?.id
    ])

    const showCrossProfileDropdown = Boolean(canUseCrossProfileEngage)
    const canConfigureCrossProfileEngage =
        !disabled && contents.length === 1 && primaryAccount !== undefined

    // Memoize the formatter so dependent callbacks keep stable refs and the dropdown avoids useless rerenders.
    const formatAccountLabel = useCallback((account: AccountRecord) => {
        return getAccountDisplayName(account)
    }, [])

    const handleCrossProfileAccountSelect = useCallback(
        async (account: AccountRecord) => {
            if (contents.length !== 1 || loading) {
                return
            }

            const accountId = account.id
            const currentLikedAccountId =
                localLikeApplied === undefined ? remoteLikedAccountId : localLikedAccountId
            const shouldUnlike = Boolean(
                effectiveActionAlreadyApplied && currentLikedAccountId === accountId
            )

            pendingSnapshotRef.current = {
                localLikeApplied,
                localLikedAccountId,
                selectedAccountId: selectedCrossProfileAccountId
            }
            pendingTargetRef.current = {
                likeApplied: !shouldUnlike,
                likedAccountId: shouldUnlike ? undefined : accountId
            }

            setSelectedCrossProfileAccountId(accountId)

            suppressLikeSuccessToastRef.current = true

            try {
                const result = await likeContentMutation({
                    variables: {
                        like: !shouldUnlike,
                        contentIds: contents.map((content) => content.id),
                        actionByAccountIdOverride: accountId
                    }
                })

                const likeResponses = result.data?.likeContent ?? []
                const anySuccess = likeResponses.some((response) => response?.success)

                if (!anySuccess) {
                    restorePendingLikeState()
                    return
                }

                const target = pendingTargetRef.current
                if (target) {
                    setLocalLikeApplied(target.likeApplied)
                    setLocalLikedAccountId(target.likedAccountId)
                    clearPendingLikeState()
                }

                const accountLabel = formatAccountLabel(account)
                if (shouldUnlike) {
                    toastSuccess(`Unlike as ${accountLabel}`)
                } else {
                    toastSuccess(`Like as ${accountLabel}`)
                }
            } catch {
                restorePendingLikeState()
            } finally {
                suppressLikeSuccessToastRef.current = false
            }
        },
        [
            contents,
            effectiveActionAlreadyApplied,
            formatAccountLabel,
            likeContentMutation,
            loading,
            localLikedAccountId,
            localLikeApplied,
            restorePendingLikeState,
            clearPendingLikeState,
            remoteLikedAccountId,
            selectedCrossProfileAccountId
        ]
    )

    // Note for `useCallback`
    // useCallback keeps formatAccountLabel’s function identity stable, which matters because several other hooks list it as a dependency. The React docs say, “useCallback will return a memoized version of the callback that only changes if one of the dependencies has changed” (React Docs: useCallback). Without it, every render would create a brand-new formatter, forcing handleCrossProfileAccountSelect and handleCrossProfileDefaultChange to rebuild, which in turn prompts the dropdown component to reevaluate even when nothing about the account label changed. The hook prevents that churn, so the popover only rerenders when its inputs actually differ.

    const handleCrossProfileDefaultChange = useCallback(
        (account: AccountRecord) => {
            if (defaultCrossProfileAccountId !== account.id) {
                toastSuccess(
                    `${formatAccountLabel(account)} set as your default cross profile account`
                )
            }

            setDefaultCrossProfileAccountId(account.id)
            setSelectedCrossProfileAccountId(account.id)

            updateEngageAccountIdMutation({
                variables: {
                    accountId: primaryAccount?.id as number,
                    engageAccountId: account.id
                }
            }).catch((error) => {
                toastError(error.message)
            })
        },
        [
            defaultCrossProfileAccountId,
            formatAccountLabel,
            primaryAccount?.id,
            updateEngageAccountIdMutation
        ]
    )

    async function handleActionClick() {
        // If only one is selected then just call the action. A user may wish to like or unlike
        if (contents.length === 1) {
            if (loading) {
                return
            }

            const nextLikeState = !effectiveActionAlreadyApplied
            pendingSnapshotRef.current = {
                localLikeApplied,
                localLikedAccountId,
                selectedAccountId: selectedCrossProfileAccountId
            }
            pendingTargetRef.current = {
                likeApplied: nextLikeState,
                likedAccountId: nextLikeState ? selectedCrossProfileAccountId : undefined
            }

            try {
                const result = await likeContentMutation({
                    variables: {
                        like: nextLikeState,
                        contentIds: contents.map((content) => content.id),
                        actionByAccountIdOverride: selectedCrossProfileAccountId
                    }
                })

                const likeResponses = result.data?.likeContent ?? []
                const anySuccess = likeResponses.some((response) => response?.success)

                if (!anySuccess) {
                    restorePendingLikeState()
                    return
                }

                const target = pendingTargetRef.current
                if (target) {
                    setLocalLikeApplied(target.likeApplied)
                    setLocalLikedAccountId(target.likedAccountId)
                    clearPendingLikeState()
                }
            } catch {
                restorePendingLikeState()
            }
            return
        } else {
            // If multiple items are liked then this is a bulk action.

            // Remove the items that have already been liked
            const contentNotAlreadyLiked = contents.filter(
                (content) =>
                    content.actions.filter(
                        (action) => action.action === ContentAction.Like && action.active === true
                    ).length === 0
            )

            // Only call the like function if there is content to like
            if (contentNotAlreadyLiked.length > 0) {
                await likeContentMutation({
                    variables: {
                        like: !effectiveActionAlreadyApplied,
                        contentIds: contentNotAlreadyLiked.map((content) => content.id),
                        actionByAccountIdOverride: selectedCrossProfileAccountId
                    }
                })
            } else {
                toastWarning('You have already liked all selected content')
            }
        }
    }

    if (!showCrossProfileDropdown) {
        return (
            <div
                className={clsx(
                    'relative transition-opacity',
                    loading && 'pointer-events-none opacity-60'
                )}
            >
                <BaseActionButton
                    tooltip="Like/unlike this comment"
                    loading={loading}
                    colour="green"
                    actionAlreadyApplied={effectiveActionAlreadyApplied}
                    icon={<HandThumbUpIcon />}
                    onActionClick={handleActionClick}
                    isActionQueued={false}
                    disabled={disabled}
                    disabledMessage={disabledMessage}
                    loadingVariant="compact"
                />
            </div>
        )
    }

    return (
        <Popover className="relative">
            {({ open, close }) => {
                const isLoading = loading
                const containerClasses = clsx(
                    'relative inline-flex h-10 items-center overflow-hidden rounded-full border focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 transition-opacity',
                    disabled
                        ? 'cursor-not-allowed border-gray-200 bg-white text-gray-300 opacity-70'
                        : effectiveActionAlreadyApplied
                          ? 'border-green-700 bg-green-700 text-green-100'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-green-100 hover:bg-green-100 hover:text-green-700 hover:shadow-md',
                    !disabled &&
                        !effectiveActionAlreadyApplied &&
                        open &&
                        'border-green-100 bg-green-100 text-green-800 shadow-md',
                    isLoading && 'pointer-events-none'
                )

                const baseButtonClasses = clsx(
                    '!bg-transparent !shadow-none hover:!bg-transparent hover:!shadow-none !text-current !w-10 !p-2 !rounded-full !flex-shrink-0',
                    effectiveActionAlreadyApplied
                        ? '!text-green-100'
                        : open
                            ? '!text-green-800'
                            : '!text-inherit'
                )

                const dropdownDisabled = isLoading || !canConfigureCrossProfileEngage

                const shouldShowDivider = effectiveActionAlreadyApplied && !isLoading

                const dropdownButtonClasses = clsx(
                    'flex h-full items-center gap-1 px-3 text-current focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-60 transition-colors border-l',
                    shouldShowDivider ? 'border-green-200 text-green-100' : 'border-l-0',
                    disabled
                        ? 'border-gray-200 text-gray-300'
                        : effectiveActionAlreadyApplied
                          ? 'text-green-100'
                          : 'border-gray-200 text-inherit hover:text-green-700 hover:border-green-600',
                    !disabled &&
                        !dropdownDisabled &&
                        !effectiveActionAlreadyApplied &&
                        isContainerHovered &&
                        'border-green-600 text-green-700',
                    !disabled &&
                        !dropdownDisabled &&
                        !effectiveActionAlreadyApplied &&
                        open &&
                        'text-green-800 border-green-600'
                )

                const chevronClasses = clsx('h-4 w-4', open ? 'rotate-180' : undefined)

                return (
                    <>
                        <div
                            className={containerClasses}
                            aria-busy={isLoading}
                            onMouseEnter={() => {
                                if (!disabled) {
                                    setIsContainerHovered(true)
                                }
                            }}
                            onMouseLeave={() => {
                                setIsContainerHovered(false)
                            }}
                        >
                            <BaseActionButton
                                tooltip="Like/unlike this comment"
                                loading={loading}
                                colour="green"
                                actionAlreadyApplied={effectiveActionAlreadyApplied}
                                icon={<HandThumbUpIcon />}
                                onActionClick={handleActionClick}
                                isActionQueued={false}
                                disabled={disabled}
                                disabledMessage={disabledMessage}
                                className={baseButtonClasses}
                                loadingVariant="compact"
                            />

                            <PopoverButton
                                aria-label="Open cross profile like options"
                                className={dropdownButtonClasses}
                                disabled={dropdownDisabled}
                                type="button"
                                aria-disabled={dropdownDisabled}
                            >
                                <ChevronDownIcon className={chevronClasses} />
                            </PopoverButton>
                        </div>

                        {canConfigureCrossProfileEngage && (
                            <PopoverPanel className="absolute right-0 z-20 mt-2 w-96 rounded-lg bg-white p-4 text-sm text-gray-600 shadow-lg ring-1 ring-black ring-opacity-5">
                                {primaryAccount && (
                                    <CrossProfileEngageSelectionComponent
                                        currentAccount={primaryAccount}
                                        selectedAccountId={
                                            selectedCrossProfileAccountId ?? primaryAccount.id
                                        }
                                        defaultAccountId={defaultCrossProfileAccountId}
                                        likedAccountId={effectiveLikedAccountId}
                                        likeApplied={effectiveActionAlreadyApplied}
                                        onAccountSelect={handleCrossProfileAccountSelect}
                                        onMakeDefault={handleCrossProfileDefaultChange}
                                        onRequestClose={() => close()}
                                    />
                                )}
                            </PopoverPanel>
                        )}
                    </>
                )
            }}
        </Popover>
    )
}
